import { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { addHours, addYears, format } from 'date-fns';
import PropTypes from 'prop-types';
import { Box, Switch, Typography } from '@mui/material';
import { apiClient } from 'config/api';
import { Alert, Button, DateTimePicker, StrongModalText } from '@atoms';
import { CSV_LINE_ERRORS_MESSAGES, PERMISSIONS } from '@globalConstants';
import {
  useCompanyDetails,
  useCustomerId,
  useIsAreasAdmin,
  usePartnerCustomers,
  usePermissions,
  useShowAreaFeature,
  useUser,
} from '@hooks';
import { BoxContainer } from '@layouts';
import { Dropzone, ErrorList } from '@molecules';
import { TemplateLinkDispersion } from '@organisms';
import { parseAmount, trackings } from '@utils';
import {
  DISPERSION_ERROR_EVENT,
  dispersionClick,
  EVENT_FILE_DISPERSION_CONFIRM,
  EVENT_FILE_DISPERSION_DELETE_FILE,
  EVENT_FILE_DISPERSION_SCHEDULE_DATE,
  EVENT_FILE_DISPERSION_SELECT_DATE,
  EVENT_FILE_DISPERSION_UPLOAD_FILE,
} from './events';
import styles from './styles';

/**
 * File dispersion tab (original content of the screen)
 */
const FileDispersionTab = ({ isLoading, setLoading, data, selectedArea }) => {
  const [dispersionDay, setDispersionDay] = useState(null);
  const [isScheduled, setIsScheduled] = useState(false);
  const [dispersionSuccess, setDispersionSuccess] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [error, setError] = useState({ title: '', description: '' });
  const [dispersionFiles, setDispersionFiles] = useState([]);
  const [fileErrorDetails, setFileErrorDetails] = useState([]);
  const { user } = useUser();
  const { customerId } = useCustomerId();
  const { data: companyData } = useCompanyDetails(customerId);
  const companyName = companyData?.description;
  const [confirmDispersion, setConfirmDispersion] = useState({
    openModal: false,
    dispersionId: null,
    total: null,
    count: null,
  });
  const { showAreaFeature } = useShowAreaFeature();
  const isDemo = usePermissions([PERMISSIONS.demoCreateDispersion]);
  const { isAreasAdmin } = useIsAreasAdmin();
  const { isLoading: isCustomersLoading, error: customersError } = usePartnerCustomers();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const today = new Date();

  const formatDay = dispersionDay && format(dispersionDay, 'dd/MM/yyyy');
  const formatTime = dispersionDay && format(dispersionDay, 'HH:mm');
  const hasSelectedFile = dispersionFiles.length > 0;
  const hasFileLineErrors = fileErrorDetails.length > 0;

  /**
   * Handle the selection of a file
   */
  const handleSelectFiles = useCallback(
    (files) => {
      dispersionClick(EVENT_FILE_DISPERSION_UPLOAD_FILE);
      if (hasFileLineErrors) {
        setFileErrorDetails([]);
      }

      setDispersionFiles(files);
    },
    [hasFileLineErrors]
  );

  /**
   * validation to disable or not dispersion send file button
   */
  const disableButton = () => {
    if (isDemo) return true;
    const validateArea = showAreaFeature && !isAreasAdmin ? !selectedArea : false;
    if (isLoading || hasFileLineErrors || isCustomersLoading || customersError || validateArea)
      return true;

    if (user.isInternal) {
      if (!customerId) return true;
    } else {
      if (!data) return true;
    }

    if (isScheduled) {
      return !(hasSelectedFile && dispersionDay);
    } else {
      return !hasSelectedFile;
    }
  };

  /**
   * Handle disperison confirmation
   * @param {boolean} confirm Whether the user confirms of cancels the dispersion
   */
  const handleConfirmation = async (confirm) => {
    setLoading(true);
    try {
      const endpoint = user?.isInternal ? '/s3/dispersion-pending' : '/s3/dispersion';
      await apiClient.put(endpoint, {
        dispersionId: confirmDispersion.dispersionId,
        confirm,
      });
      if (confirm) {
        setDispersionSuccess(true);
        setDispersionDay(null);
        setDispersionFiles([]);
      }
    } catch (error) {
      const response = error.response;
      if (confirm) {
        if (response?.status === 404 && response.data?.errors?.[0]?.message === 'Key not found') {
          setError({
            title: 'DISPERSIONS:ERRORS:EXPIRED_TITLE',
            description: 'DISPERSIONS:ERRORS:EXPIRED_DESCRIPTION',
          });
        } else {
          setError({
            title: 'DISPERSIONS:ERRORS:CONFIRMATION_ERROR_TITLE',
            description: 'DISPERSIONS:ERRORS:CONFIRMATION_ERROR_DESCRIPTION',
          });
        }
        setErrorOpen(true);
      }
    }
    setConfirmDispersion({ dispersionId: null, openModal: false, total: null, count: null });
    setLoading(false);
  };

  /**
   * send file to be
   * TODO: Move to services.js
   */
  const handleSubmit = async () => {
    dispersionClick(EVENT_FILE_DISPERSION_CONFIRM);

    setLoading(true);
    try {
      if (!(await checkAvailableTime())) return;

      const formData = new FormData();
      formData.append('file', dispersionFiles[0]);
      formData.append('customerId', customerId);
      selectedArea && formData.append('areaId', selectedArea);

      dispersionDay && formData.append('date', format(dispersionDay, 'yyyyMMddHH'));

      const url = '/v3/dispersion/file';
      const body = formData;

      const {
        data: { dispersionId, total, count },
      } = await apiClient.post(url, body, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setConfirmDispersion({ openModal: true, dispersionId, total, count });
    } catch (error) {
      const response = error.response;
      trackings(DISPERSION_ERROR_EVENT(error));
      if (response?.status === 400) {
        setErrorOpen(true);
        if (
          response?.data.code === 'csv_line_errors' ||
          response?.data.code === 'file_line_errors'
        ) {
          const fileErrors = response.data.data?.data ?? response.data.data;
          setFileErrorDetails(fileErrors);
          setError({
            title: 'DISPERSIONS:ERRORS:FILE_CONTAINS_ERRORS_TITLE',
            description: 'DISPERSIONS:ERRORS:FILE_CONTAINS_ERRORS_DESCRIPTION',
          });
        } else if (response.data.code === 'csv_record_inconsistent_columns') {
          setError({
            title: 'COMMON:VALIDATIONS:INCONSISTENT_COLUMNS_TITLE',
            description: t('COMMON:VALIDATIONS:INCONSISTENT_COLUMNS_DESCRIPTION', {
              line: response.data.data.line,
            }),
          });
        } else {
          const code = response.data?.errors[0]?.message;
          switch (code) {
            case 'Filename is not accepted.':
              setError({
                title: 'DISPERSIONS:ERRORS:INVALID_FILE_NAME_TITLE',
                description: 'DISPERSIONS:ERRORS:INVALID_FILE_NAME_DESCRIPTION',
              });
              break;
            case 'Account balance is less than dispersion amount.':
            case 'Account available balance is less than dispersion amount.':
              setError({
                title: 'DISPERSIONS:ERRORS:INSUFFICIENT_BALANCE_TITLE',
                description: 'DISPERSIONS:ERRORS:INSUFFICIENT_BALANCE_DESCRIPTION',
              });
              break;
            case 'Area balance is less than dispersion amount.':
              setError({
                title: 'DISPERSIONS:ERRORS:INSUFFICIENT_BALANCE_TITLE',
                description: 'DISPERSIONS:ERRORS:NOT_ENOUGH_BALANCE_AREA',
              });
              break;
            case 'Another dispersion is already processing': {
              setError({
                title: 'DISPERSIONS:ERRORS:ALREADY_PROCESSING_TITLE',
                description: 'DISPERSIONS:ERRORS:ALREADY_PROCESSING_DESCRIPTION',
              });
              break;
            }
            case 'Another dispersion is scheduled at the same time': {
              setError({
                title: 'DISPERSIONS:ERRORS:DUPLICATE_TIME_FOR_SCHEDULED_TITLE',
                description: 'DISPERSIONS:ERRORS:DUPLICATE_TIME_FOR_SCHEDULED_DESCRIPTION',
              });
              break;
            }
            default:
              setError({
                title: 'COMMON:ERRORS:GENERIC_ERROR_TITLE',
                description: 'DISPERSIONS:ERRORS:DEFAULT_BAD_REQUEST_ERROR',
              });
              break;
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  /** Check if the selected or current time is available to do a dispersion
   *
   * @returns false if the time is unavailable
   */
  const checkAvailableTime = async () => {
    const {
      data: { serverTime },
    } = await apiClient.get('/internal/current-time');
    const currentTime = new Date(serverTime);

    const hours = (dispersionDay ?? currentTime).getHours();
    const minutes = currentTime.getMinutes();
    if (dispersionDay && dispersionDay < currentTime) {
      setErrorOpen(true);
      setError({
        title: 'DISPERSIONS:ERRORS:UNAVAILABLE_TIME_TITLE',
        description: 'DISPERSIONS:ERRORS:INVALID_DATE',
      });
      return false;
    }
    if ((dispersionDay && hours === 0) || (hours === 0 && minutes < 30)) {
      setErrorOpen(true);
      setError({
        title: 'DISPERSIONS:ERRORS:UNAVAILABLE_TIME_TITLE',
        description: `DISPERSIONS:ERRORS:${
          dispersionDay ? 'SCHEDULE_' : ''
        }UNAVAILABLE_TIME_DESCRIPTION`,
      });
      return false;
    }
    return true;
  };
  /**
   * Handle the switch to change the dispersion date
   */
  const handleScheduled = () => {
    dispersionClick(EVENT_FILE_DISPERSION_SCHEDULE_DATE);
    setIsScheduled((prevState) => !prevState);
    setDispersionDay(null);
  };

  return (
    <BoxContainer>
      <Box sx={styles.paperHead}>
        <Typography variant='h4'>{t('DISPERSIONS:UPLOAD_FILE')}</Typography>
        <TemplateLinkDispersion customerId={customerId} areaId={selectedArea} />
      </Box>
      <Dropzone
        files={dispersionFiles}
        setFiles={setDispersionFiles}
        disabled={isLoading}
        handleFilesSelection={handleSelectFiles}
        onClear={() => {
          dispersionClick(EVENT_FILE_DISPERSION_DELETE_FILE);
        }}
      />
      <Box sx={styles.scheduleContainer}>
        <Box sx={styles.scheduleText}>
          <Typography variant='h4'>{t('DISPERSIONS:SCHEDULE_TITLE')}</Typography>
          <Typography variant='body2' color='gray.90'>
            {t('DISPERSIONS:SCHEDULE_TEXT')}
          </Typography>
        </Box>
        <Switch checked={isScheduled} onChange={handleScheduled} />

        {isScheduled && (
          <DateTimePicker
            hideToolbar
            value={dispersionDay}
            handleChange={(newValue) => {
              dispersionClick(EVENT_FILE_DISPERSION_SELECT_DATE);
              setDispersionDay(newValue);
            }}
            inputLabel={'DISPERSIONS:INPUT_LABEL_DATE'}
            minDateTime={addHours(today, 1)}
            maxDate={addYears(today, 3)}
            calendarViews={['year', 'month', 'day', 'hours']}
          />
        )}
      </Box>
      <Button
        text={'COMMON:FILE_UPLOADS:SEND_FILE_BUTTON'}
        variant='contained'
        color='primary'
        onClick={handleSubmit}
        disabled={disableButton()}
        isLoading={isLoading}
        size='large'
      />
      {/* TODO - Replace Alert by DispersionConfirmationModal componnet orgnisms*/}
      <Alert
        variant='warning'
        modalVisible={confirmDispersion.openModal}
        handleClose={() => handleConfirmation(false)}
        handleSubmit={() => handleConfirmation(true)}
        closeButtonText='COMMON:CANCEL'
        title={t('DISPERSIONS:MODAL_TITLE')}
        submitLoading={isLoading}
        disabledConfirm={disableButton()}
        content={
          <Typography color='gray.90'>
            <Trans
              i18nKey={
                isScheduled
                  ? 'DISPERSIONS:MODAL_MESSAGE_SCHEDULED'
                  : 'DISPERSIONS:MODAL_MESSAGE_INSTANT'
              }
              components={{ bold: <StrongModalText /> }}
              values={{
                total: parseAmount(confirmDispersion.total),
                count: confirmDispersion.count,
                formatDay,
                formatTime,
                companyName,
              }}
            />
          </Typography>
        }
      />
      <Alert
        modalVisible={dispersionSuccess}
        handleClose={() => setDispersionSuccess(false)}
        variant='info'
        title={
          isScheduled
            ? 'DISPERSIONS:DISPERSIONS_SUCCESS_TITLE_SCHEDULED'
            : 'DISPERSIONS:DISPERSIONS_SUCCESS_TITLE_INSTANT'
        }
        description={
          isScheduled
            ? 'DISPERSIONS:DISPERSIONS_SUCCESS_BODY_SCHEDULED'
            : 'DISPERSIONS:DISPERSIONS_SUCCESS_BODY_INSTANT'
        }
        confirmButtonText={'DISPERSIONS:GO_TO_DISPERSIONS'}
        handleSubmit={() => {
          navigate('/dispersiones/mis-dispersiones');
        }}
        customStyles={{ description: { whiteSpace: 'pre-line' } }}
        closeButtonText={'COMMON:CLOSE'}
      />
      <Alert
        modalVisible={errorOpen}
        handleClose={() => setErrorOpen(false)}
        description={error.description}
        title={error.title}
        widePaper={hasFileLineErrors}
        content={
          hasFileLineErrors && (
            <ErrorList
              errors={fileErrorDetails}
              title={'DISPERSIONS:CSV_ERRORS_TITLE'}
              getMessages={CSV_LINE_ERRORS_MESSAGES}
            />
          )
        }
      />
    </BoxContainer>
  );
};

FileDispersionTab.propTypes = {
  isLoading: PropTypes.bool,
  setLoading: PropTypes.func,
  data: PropTypes.object,
  selectedArea: PropTypes.string,
};

export default FileDispersionTab;
